import { useEffect, useState, useCallback } from "react";
import { X, CaretLeft, CaretRight, Image as ImageIcon } from "@phosphor-icons/react";

/**
 * ZApiGuide — visual onboarding carousel for connecting Z-API.
 *
 * Premium overlay strategy:
 *   - Real screenshots from the Z-API dashboard live untouched in /public.
 *   - Annotations (dashed pulsing rings, arrows, numbered badges, callouts)
 *     are rendered as SVG layers on top, so they stay crisp at any zoom and
 *     can be tweaked without re-editing images.
 *
 * All coordinates are in % of the image's natural 1448×1086 space —
 * resolution-independent.
 */

// ─── Slide data (the 5 steps the operator follows in the Z-API panel) ───
//
// All ring coordinates are in % of the image's natural 1448x1086 box
// (cx, cy = center; rx, ry = radii). These were measured directly from
// each screenshot — do NOT eyeball-edit without re-measuring. The Z-API
// dashboard layout is dense, so a 2-3% drift visibly misses the target.
const SLIDES = [
  {
    id: "instancias",
    src: "/zapi-guide/01-dashboard.png",
    title: "1. Abra Instâncias Web",
    caption:
      "Faça login em app.z-api.io. No menu lateral esquerdo (MENU PRINCIPAL), clique em \"Instâncias Web\" pra ver suas instâncias.",
    rings: [
      // Sidebar item "Instâncias Web" — measured: cx=9.5%, cy=22.5%, w≈9%
      { cx: 9.5, cy: 22.5, rx: 5.0, ry: 1.7, label: "1" },
    ],
  },
  {
    id: "lista",
    src: "/zapi-guide/02-instancias.png",
    title: "2. Clique na sua instância",
    caption:
      "Você verá uma linha com o nome dela (ex.: \"Meu número\") com Status, ID e Token. Clique nessa linha pra abrir os dados de credencial.",
    rings: [
      // The instance row "Meu número" — measured: cx≈52.5%, cy≈44.7%, w≈76%
      { cx: 52.5, cy: 44.7, rx: 38, ry: 2.4, label: "2" },
    ],
  },
  {
    id: "credenciais",
    src: "/zapi-guide/03-instancia-detalhes.png",
    title: "3. Copie os 2 valores e escaneie o QR Code",
    caption:
      "Copie a *API da instância* (URL completa) e o *Token da instância* (string hexadecimal abaixo) — esses são os 2 primeiros campos no AgentUS. Depois, escaneie o QR Code com o WhatsApp do celular do seu negócio.",
    rings: [
      // "API da instância" URL row — cx≈48.3%, cy≈30.4%, w≈62%
      { cx: 48.3, cy: 30.4, rx: 31, ry: 2.4, label: "A", color: "emerald" },
      // "Token da instância" value — cx≈66.2%, cy≈39.7%, w≈17%
      { cx: 66.2, cy: 39.7, rx: 13, ry: 2.4, label: "B", color: "emerald" },
      // QR Code on the right — cx≈86.3%, cy≈55.3%, w≈14%, h≈18%
      { cx: 86.3, cy: 55.3, rx: 8.5, ry: 11.0, label: "C", color: "sky" },
    ],
  },
  {
    id: "menu-seguranca",
    src: "/zapi-guide/01-dashboard.png",
    title: "4. Volte ao menu e abra Segurança",
    caption:
      "Atenção — o próximo token (Token de segurança da conta) NÃO está dentro da instância. Volte ao menu lateral e clique em \"Segurança\".",
    rings: [
      // Sidebar item "Segurança" — measured: cx=9.5%, cy=34.0%, w≈8.5%
      { cx: 9.5, cy: 34.0, rx: 5.0, ry: 1.7, label: "4" },
    ],
  },
  {
    id: "token-seguranca",
    src: "/zapi-guide/04-seguranca.png",
    title: "5. Copie o Token de segurança da conta",
    caption:
      "Role até a seção \"3. Token de segurança da conta\". Copie o valor mostrado no campo (em fonte monoespaçada). Esse valor entra no 3º campo do AgentUS. Se sua conta não usa esse recurso, deixe em branco no AgentUS.",
    rings: [
      // Token input field — cx≈33.4%, cy≈80.1%, w≈26%, h≈5%
      { cx: 33.4, cy: 80.1, rx: 14.0, ry: 3.0, label: "D", color: "emerald" },
      // Regenerate button (↻) — cx≈49.0%, cy≈80.1%, ~2.8% diameter
      { cx: 49.0, cy: 80.1, rx: 1.8, ry: 2.4, label: "↻", color: "amber" },
    ],
    note:
      "Não tem o token anotado? Clique no botão de reload (↻) ao lado pra gerar um novo — o token anterior deixa de funcionar imediatamente.",
  },
];

// Color tokens — keep palette aligned with the rest of the app
const RING_COLORS = {
  red:     { stroke: "#dc2626", fill: "rgba(220,38,38,0.08)", badge: "bg-red-600" },
  emerald: { stroke: "#059669", fill: "rgba(5,150,105,0.08)",  badge: "bg-emerald-600" },
  sky:     { stroke: "#0284c7", fill: "rgba(2,132,199,0.08)",  badge: "bg-sky-600" },
  amber:   { stroke: "#d97706", fill: "rgba(217,119,6,0.08)",  badge: "bg-amber-600" },
};

/* Annotations REMOVED — see Slide() for the textual hints overlay we use
   instead. Keeping the file's structure for future use; the function below
   intentionally renders nothing so we never again ship miss-positioned
   rings over real screenshots. */
function Annotations() { return null; }
// eslint-disable-next-line no-unused-vars
function _unusedAnnotations({ slide }) {
  // Renders the SVG overlay sized exactly to the image's natural box.
  // viewBox is 100×100 so all coords (% of 1448×1086) map 1:1.
  // We keep the natural aspect ratio (1448/1086 ≈ 1.333) by giving the
  // wrapper that exact aspect-ratio and letting the SVG fill it.
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    >
      <defs>
        <filter id="zg-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="0.4" />
        </filter>
      </defs>

      {(slide.arrows || []).map((a, i) => (
        <g key={`arrow-${i}`}>
          <line
            x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2}
            stroke="#dc2626" strokeWidth="0.45"
            strokeLinecap="round"
          />
          <polygon
            points={`${a.x2},${a.y2} ${a.x2 + 1.2},${a.y2 - 0.9} ${a.x2 + 0.4},${a.y2 - 1.6}`}
            fill="#dc2626"
            transform={`rotate(${Math.atan2(a.y2 - a.y1, a.x2 - a.x1) * 180 / Math.PI - 45}, ${a.x2}, ${a.y2})`}
          />
        </g>
      ))}

      {(slide.rings || []).map((r, i) => {
        const c = RING_COLORS[r.color || "red"];
        return (
          <g key={`ring-${i}`}>
            {/* halo */}
            <ellipse
              cx={r.cx} cy={r.cy} rx={r.rx + 0.6} ry={r.ry + 0.6}
              fill="none" stroke={c.stroke} strokeWidth="0.18" opacity="0.35"
              filter="url(#zg-glow)"
            />
            {/* main dashed ring */}
            <ellipse
              cx={r.cx} cy={r.cy} rx={r.rx} ry={r.ry}
              fill={c.fill} stroke={c.stroke} strokeWidth="0.35"
              strokeDasharray="0.9 0.7" strokeLinecap="round"
            >
              <animate attributeName="stroke-dashoffset"
                       from="0" to="-3.2" dur="2.6s" repeatCount="indefinite" />
            </ellipse>
          </g>
        );
      })}

      {/* numbered badges, rendered last so they sit on top */}
      {(slide.rings || []).map((r, i) => {
        const c = RING_COLORS[r.color || "red"];
        // Place badge just outside the ring at top-right.
        const bx = r.cx + r.rx + 0.3;
        const by = r.cy - r.ry - 0.3;
        return (
          <g key={`badge-${i}`}>
            <circle cx={bx} cy={by} r="1.4" fill={c.stroke} stroke="#fff" strokeWidth="0.15" />
            <text
              x={bx} y={by + 0.55}
              textAnchor="middle"
              fontSize="1.6"
              fontWeight="700"
              fill="#fff"
              style={{ fontFamily: "ui-sans-serif, system-ui" }}
            >
              {r.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function Slide({ slide, index, total }) {
  return (
    <div className="w-full flex flex-col">
      {/* Image with overlay */}
      <div className="relative rounded-md overflow-hidden bg-slate-100 border border-slate-200"
           style={{ aspectRatio: "1448 / 1086" }}>
        <img
          src={slide.src}
          alt={slide.title}
          className="absolute inset-0 w-full h-full object-cover select-none"
          draggable={false}
        />
        <Annotations slide={slide} />
      </div>

      {/* Caption */}
      <div className="mt-4 px-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-medium">
            Passo {index + 1} de {total}
          </span>
        </div>
        <h4 className="font-heading text-[17px] text-slate-900 font-semibold tracking-tight mt-1">
          {slide.title}
        </h4>
        <p className="text-[13.5px] text-slate-600 mt-1.5 leading-relaxed">
          {slide.caption}
        </p>
        {slide.note && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-[12.5px] text-amber-900 leading-relaxed">
            {slide.note}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Public API: <ZApiGuideTrigger /> ───────────────────────────────────────
//
// Usage:
//   <ZApiGuideTrigger />
//
// Drops a "Ver guia visual" pill button that opens the carousel modal.

export function ZApiGuideTrigger({ initialIndex = 0, label = "Ver guia visual", className = "" }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(initialIndex);

  const close = useCallback(() => setOpen(false), []);
  const prev = useCallback(() => setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length), []);
  const next = useCallback(() => setIdx((i) => (i + 1) % SLIDES.length), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close, prev, next]);

  return (
    <>
      <button
        type="button"
        onClick={() => { setIdx(initialIndex); setOpen(true); }}
        data-testid="zapi-guide-open"
        className={`inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3.5 py-1.5 text-[12px] font-medium hover:bg-slate-800 transition-colors ${className}`}
      >
        <ImageIcon size={13} weight="fill" />
        {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
          data-testid="zapi-guide-modal"
        >
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
            onClick={close}
            aria-hidden="true"
          />

          {/* dialog */}
          <div
            className="relative bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Guia visual Z-API"
          >
            {/* header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-slate-900 text-white flex items-center justify-center">
                  <ImageIcon size={14} weight="fill" />
                </div>
                <div>
                  <div className="text-[13.5px] font-semibold text-slate-900 tracking-tight">Guia visual — conectar WhatsApp via Z-API</div>
                  <div className="text-[11px] text-slate-500">Use as setas ← → do teclado pra navegar</div>
                </div>
              </div>
              <button
                onClick={close}
                data-testid="zapi-guide-close"
                className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-900 transition-colors"
                aria-label="Fechar"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            {/* body — scrollable */}
            <div className="flex-1 overflow-y-auto px-5 py-5">
              <Slide slide={SLIDES[idx]} index={idx} total={SLIDES.length} />
            </div>

            {/* footer — dots + prev/next */}
            <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-200 bg-slate-50">
              <button
                onClick={prev}
                data-testid="zapi-guide-prev"
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 px-3.5 py-1.5 text-[12px] font-medium text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <CaretLeft size={11} weight="bold" />
                Anterior
              </button>

              <div className="flex items-center gap-1.5">
                {SLIDES.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setIdx(i)}
                    aria-label={`Ir para passo ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${
                      i === idx ? "w-6 bg-slate-900" : "w-1.5 bg-slate-300 hover:bg-slate-400"
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={next}
                data-testid="zapi-guide-next"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3.5 py-1.5 text-[12px] font-medium hover:bg-slate-800 transition-colors"
              >
                {idx === SLIDES.length - 1 ? "Recomeçar" : "Próximo"}
                <CaretRight size={11} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ZApiGuideTrigger;
